// What a resource's button does, for every place a resource shows (Resources, a week's worksheet and
// prompts), by its kind (decision 8):
// - file: a link sign-file signs for 2 minutes, opened at once in this tab, where it downloads under
//   its own name; nothing is kept
// - tool: its page in the hub (card.href, /<product>/tools/<placement id>), opened in a new tab,
//   where the tool runner signs the file and runs it (ToolPage.jsx). Supabase Storage answers an
//   HTML file as plain text, so the signed link itself would show the tool's source
// - text: copied to the clipboard
// - link: opened in a new tab, or a page inside the hub
// - video: played on the page (the screen draws the player)
// Opens per resource are recorded: sign-file records files and tools (a tool when its page signs it),
// the rest are recorded here (hub.log_resource_open). While staff view as a member, files and tools
// don't open (sign-file refuses) and nothing is recorded; copying and links still work.
import { useState } from 'react'
import { navigate } from '../lib/router.jsx'
import { copyText } from '../lib/clipboard.js'
import { HubFunctionError } from '../lib/functions.js'
import { useViewingAs, VIEWING_AS_LINE } from '../lib/viewAs.jsx'
import { logOpen, signPlacement } from './data.js'

const FALLBACK = 'Something went wrong. Try again.'
export const COPY_FAILED = "That couldn't be copied. Select the text and copy it yourself."

// { status: 'idle' | 'busy' | 'done' | 'failed', line, run, blocked }
export function useResourceAction(card) {
  const viewingAs = useViewingAs()
  const [s, set] = useState({ status: 'idle', line: null })
  const kind = card?.action?.kind
  const blocked = viewingAs && (kind === 'download' || kind === 'tool')
  const log = action => { if (!viewingAs) logOpen(card.id, action).catch(() => { /* never holds the member up */ }) }

  function run(e) {
    if (!card?.action || s.status === 'busy') return
    if (blocked) { e?.preventDefault?.(); set({ status: 'failed', line: VIEWING_AS_LINE }); return }
    if (kind === 'tool') {
      // the link opens the tool's page in a new tab (ResourceButton draws it as a link)
      if (!card.href) { e?.preventDefault?.(); set({ status: 'failed', line: FALLBACK }); return }
      set({ status: 'done', line: null })
      return
    }
    if (kind === 'download') {
      set({ status: 'busy', line: null })
      signPlacement(card.id)
        .then(data => {
          if (typeof data?.url !== 'string' || !data.url.startsWith('https://')) throw new Error('no link')
          window.location.assign(data.url)
          set({ status: 'done', line: null })
        })
        .catch(err => {
          set({ status: 'failed', line: err instanceof HubFunctionError ? err.message : FALLBACK })
        })
      return
    }
    if (kind === 'copy') {
      copyText(card.text || '').then(ok => {
        set(ok ? { status: 'done', line: null } : { status: 'failed', line: COPY_FAILED })
        if (ok) log('copy')
      })
      return
    }
    if (kind === 'link') {
      log('link')
      if (card.url && card.url.startsWith('/')) { e?.preventDefault?.(); navigate(card.url); return }
      set({ status: 'done', line: null })
      return
    }
    if (kind === 'video') {
      log('video')
      set(prev => ({ status: prev.status === 'done' ? 'idle' : 'done', line: null }))
    }
  }

  return { ...s, run, blocked }
}
